import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PriceExportPreviewModal = ({ isOpen, onClose, orgId, exportRow }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !orgId || !exportRow?.id) return;
      setLoading(true);
      setError('');
      setRows([]);
      try {
        const { data, error } = await supabase
          .from('epos_price_history')
          .select('id, sku, old_price, new_price, changed_at, epos_products(name), parsed_invoices(vendor_name, invoice_number)')
          .eq('org_id', orgId)
          .eq('export_id', exportRow.id)
          .order('changed_at', { ascending: true });
        if (error) throw error;
        setRows(data || []);
      } catch (e) {
        setError(e?.message || 'Failed to load export details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, orgId, exportRow?.id]);

  const totals = useMemo(() => {
    return rows.reduce((acc, row) => {
      const oldP = toNum(row?.old_price) || 0;
      const newP = toNum(row?.new_price) || 0;
      acc.old += oldP;
      acc.new += newP;
      acc.variance += (newP - oldP);
      return acc;
    }, { old: 0, new: 0, variance: 0 });
  }, [rows]);

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Eye" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Export Details</h2>
              <p className="text-xs text-muted-foreground">
                {fmtDateTime(exportRow?.created_at)} • {rows.length} item{rows.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          {error && <div className="text-sm text-destructive mb-2">{error}</div>}
          {loading ? (
            <div className="text-sm">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No items found for this export.</div>
          ) : (
            <div className="overflow-auto border border-border rounded-md">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-muted text-muted-foreground">
                    <Th>Vendor</Th>
                    <Th>SKU</Th>
                    <Th>Product</Th>
                    <Th className="text-right">Old Cost</Th>
                    <Th className="text-right">New Cost</Th>
                    <Th className="text-right">Variance</Th>
                    <Th>Invoice</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const oldP = toNum(row?.old_price);
                    const newP = toNum(row?.new_price);
                    const variance = (oldP != null && newP != null) ? (newP - oldP) : null;
                    return (
                      <tr key={row.id} className="border-t border-border">
                        <Td>{row?.parsed_invoices?.vendor_name || 'Unknown'}</Td>
                        <Td mono>{row?.sku || ''}</Td>
                        <Td>{row?.epos_products?.name || ''}</Td>
                        <Td num>{fmtMoney(oldP)}</Td>
                        <Td num>{fmtMoney(newP)}</Td>
                        <Td num>
                          {variance != null ? (
                            <span className={variance >= 0 ? 'text-success' : 'text-destructive'}>
                              {variance > 0 ? '+' : ''}{fmtMoney(variance)}
                            </span>
                          ) : ''}
                        </Td>
                        <Td>{row?.parsed_invoices?.invoice_number || ''}</Td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/40">
                    <Td colSpan={3} className="font-medium">Totals</Td>
                    <Td num className="font-medium">{fmtMoney(totals.old)}</Td>
                    <Td num className="font-medium">{fmtMoney(totals.new)}</Td>
                    <Td num className="font-medium">{fmtMoney(totals.variance)}</Td>
                    <Td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document?.body) {
    return createPortal(modal, document.body);
  }
  return modal;
};

const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false, className = '' }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-[11px]' : ''} ${num ? 'text-right' : ''} ${className}`}>{children}</td>
);

function fmtDateTime(iso) { try { const d = new Date(iso); if (Number.isNaN(d.getTime())) return iso || ''; return d.toLocaleString('en-GB'); } catch { return iso || ''; } }
function toNum(v) { if (v === undefined || v === null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function fmtMoney(v) { if (v == null) return ''; const n = Number(v); if (!Number.isFinite(n)) return ''; return currencyFormatter.format(n); }

export default PriceExportPreviewModal;
